import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { MatSnackBar, MatSnackBarHorizontalPosition, MatSnackBarVerticalPosition } from '@angular/material/snack-bar';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { firstValueFrom } from 'rxjs';
import { AuthorizedApiService } from '@core';
import { AuthorizationService } from '@core/service/authorization.service';
import { BreadcrumbComponent } from '@shared/components/breadcrumb/breadcrumb.component';
import {
  ColumnDefinition,
  MasterTableComponent,
  TableAction,
  TableFilterDefinition,
} from '@shared/components/master-table/master-table.component';
import { ConfirmDeleteDialogComponent } from '@shared/components/confirm-delete-dialog/confirm-delete-dialog.component';
import { extractApiErrorMessage } from '@shared/utils/api-error.util';
import { EmployeeRecord } from '../../organization/employees/employee.interface';
import { CompaniesRecord } from '../../organization/companies-records/companies-record.interface';
import { AttendanceAttemptRecord } from './attendance-attempt.interface';

interface EmployeesResponse {
  data?: EmployeeRecord[];
  items?: EmployeeRecord[];
  results?: EmployeeRecord[];
  employees?: EmployeeRecord[];
}

interface CompaniesResponse { items?: CompaniesRecord[]; data?: CompaniesRecord[]; results?: CompaniesRecord[]; companiesRecords?: CompaniesRecord[]; }

interface AttendanceAttemptsResponse {
  data?: AttendanceAttemptRecord[];
  items?: AttendanceAttemptRecord[];
  results?: AttendanceAttemptRecord[];
  attempts?: AttendanceAttemptRecord[];
}

@Component({
  selector: 'app-attendance-attempt',
  standalone: true,
  imports: [BreadcrumbComponent, MasterTableComponent, TranslateModule],
  templateUrl: './attendance-attempt.component.html',
  styleUrl: './attendance-attempt.component.scss',
})
export class AttendanceAttemptComponent implements OnInit {
  dataSource = new MatTableDataSource<AttendanceAttemptRecord>([]);
  employees: EmployeeRecord[] = [];
  companies: CompaniesRecord[] = [];
  allAttempts: AttendanceAttemptRecord[] = [];
  isLoading = true;
  actions: TableAction<AttendanceAttemptRecord>[] = [];
  columnDefinitions: ColumnDefinition[] = [];
  tableFilters: TableFilterDefinition<AttendanceAttemptRecord>[] = [];
  searchValue = '';
  activeFilters: Record<string, string | number | boolean | null | Array<string | number | boolean>> = { source: [] };

  private readonly apiService = inject(AuthorizedApiService);
  private readonly authorizationService = inject(AuthorizationService);
  private readonly dialog = inject(MatDialog);
  private readonly router = inject(Router);
  private readonly snackBar = inject(MatSnackBar);
  private readonly translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);
  private searchDebounceHandle: ReturnType<typeof setTimeout> | null = null;
  private employeeSearchDebounceHandle: ReturnType<typeof setTimeout> | null = null;

  ngOnInit(): void {
    this.configureTranslations();
    this.translate.onLangChange.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.configureTranslations();
      this.tableFilters = this.buildTableFilters();
      this.allAttempts = this.allAttempts.map((attempt) => this.normalizeAttempt(attempt));
      this.applyLocalFilters();
    });
    void this.initializeData();
  }

  async initializeData(): Promise<void> {
    this.isLoading = true;
    try {
      await this.loadCompanies();
      await this.loadEmployees();
      this.tableFilters = this.buildTableFilters();
      await this.loadData();
    } finally {
      this.isLoading = false;
    }
  }

  async loadData(): Promise<void> {
    this.isLoading = true;
    try {
      const response = await firstValueFrom(
        this.apiService.get<AttendanceAttemptRecord[] | AttendanceAttemptsResponse>('attendance/attempts', {
          params: {
            ...(this.getActiveCompanyId() ? { companiesRecordId: this.getActiveCompanyId() } : {}),
            ...(this.getActiveEmployeeId() ? { employeeId: this.getActiveEmployeeId() } : {}),
            ...(this.getActiveReasonCode() ? { reasonCode: this.getActiveReasonCode() } : {}),
          },
        })
      );

      this.allAttempts = this.extractAttempts(response).map((attempt) => this.normalizeAttempt(attempt));
      this.applyLocalFilters();
      this.tableFilters = this.buildTableFilters();
    } catch (error) {
      console.error('Error loading attendance attempts', error);
      this.allAttempts = [];
      this.dataSource.data = [];
      this.showNotification(
        'snackbar-danger',
        extractApiErrorMessage(error, this.t('attendanceAttemptsCatalog.notifications.loadError')),
        'bottom',
        'center'
      );
    } finally {
      this.isLoading = false;
    }
  }

  private extractAttempts(
    response: AttendanceAttemptRecord[] | AttendanceAttemptsResponse | null | undefined
  ): AttendanceAttemptRecord[] {
    if (Array.isArray(response)) {
      return response;
    }

    if (!response || typeof response !== 'object') {
      return [];
    }

    if (Array.isArray(response.data)) {
      return response.data;
    }

    if (Array.isArray(response.items)) {
      return response.items;
    }

    if (Array.isArray(response.results)) {
      return response.results;
    }

    if (Array.isArray(response.attempts)) {
      return response.attempts;
    }

    return [];
  }

  handleAdd(): void {
    if (!this.hasAccess('create')) {
      return;
    }
    void this.router.navigate(['/attendance/attendance-attempts/new']);
  }

  async handleView(row: AttendanceAttemptRecord): Promise<void> {
    if (!this.hasAccess('read')) {
      return;
    }
    await this.router.navigate(['/attendance/attendance-attempts', row.attendanceAttemptId]);
  }

  handleDelete(row: AttendanceAttemptRecord): void {
    if (!this.hasAccess('delete')) {
      return;
    }

    const dialogRef = this.dialog.open(ConfirmDeleteDialogComponent, {
      width: '420px',
      data: {
        title: this.t('attendanceAttemptsCatalog.dialog.deleteTitle'),
        message: this.t('attendanceAttemptsCatalog.dialog.deleteMessage', { event: row.employeeNumber }),
        confirmLabel: this.t('attendanceAttemptsCatalog.dialog.deleteConfirm'),
        cancelLabel: this.t('common.cancel'),
      },
    });

    dialogRef.afterClosed().subscribe(async (confirmed: boolean) => {
      if (!confirmed) {
        return;
      }

      this.isLoading = true;
      try {
        await firstValueFrom(this.apiService.delete(`attendance/attempts/${row.attendanceAttemptId}`));
        await this.loadData();
        this.showNotification('snackbar-success', this.t('attendanceAttemptsCatalog.notifications.deleted'), 'bottom', 'center');
      } catch (error) {
        console.error('Error deleting attendance attempt', error);
        this.showNotification(
          'snackbar-danger',
          extractApiErrorMessage(error, this.t('attendanceAttemptsCatalog.notifications.deleteError')),
          'bottom',
          'center'
        );
      } finally {
        this.isLoading = false;
      }
    });
  }

  handleBulkDelete(rows: AttendanceAttemptRecord[]): void {
    if (!this.hasAccess('delete') || !rows.length) {
      return;
    }

    const dialogRef = this.dialog.open(ConfirmDeleteDialogComponent, {
      width: '420px',
      data: {
        title: this.t('common.bulkDeleteTitle'),
        message: this.t('common.bulkDeleteMessage', { count: rows.length }),
        confirmLabel: this.t('common.delete'),
        cancelLabel: this.t('common.cancel'),
      },
    });

    dialogRef.afterClosed().subscribe(async (confirmed: boolean) => {
      if (!confirmed) {
        return;
      }

      this.isLoading = true;
      try {
        await Promise.all(rows.map((row) => firstValueFrom(this.apiService.delete(`attendance/attempts/${row.attendanceAttemptId}`))));
        await this.loadData();
        this.showNotification('snackbar-success', this.t('common.bulkDeleteSuccess', { count: rows.length }), 'bottom', 'center');
      } catch (error) {
        console.error('Error deleting attendance attempts', error);
        this.showNotification(
          'snackbar-danger',
          extractApiErrorMessage(error, this.t('attendanceAttemptsCatalog.notifications.deleteError')),
          'bottom',
          'center'
        );
      } finally {
        this.isLoading = false;
      }
    });
  }

  onSearchChange(search: string): void {
    this.searchValue = search.trim().toLowerCase();
    if (this.searchDebounceHandle) {
      clearTimeout(this.searchDebounceHandle);
    }
    this.searchDebounceHandle = setTimeout(() => this.applyLocalFilters(), 250);
  }

  onFiltersChange(filters: Record<string, string | number | boolean | null | Array<string | number | boolean>>): void {
    // Ignore the retired filter if it remains in the persisted master-table state.
    const { administrativeStatus: _administrativeStatus, ...visibleFilters } = filters;
    const previousCompanyId = this.getActiveCompanyId();
    this.activeFilters = visibleFilters;
    if (previousCompanyId !== this.getActiveCompanyId()) {
      this.activeFilters['employee'] = null;
      void this.loadEmployees().then(() => this.loadData());
      return;
    }
    void this.loadData();
  }

  onFilterSearchChange(event: { filterId: string; search: string }): void {
    if (event.filterId !== 'employee') {
      return;
    }

    if (this.employeeSearchDebounceHandle) {
      clearTimeout(this.employeeSearchDebounceHandle);
    }

    this.employeeSearchDebounceHandle = setTimeout(() => {
      void this.loadEmployees(event.search);
    }, 250);
  }

  private async loadEmployees(search = ''): Promise<void> {
    try {
      const selectedEmployeeId = this.getActiveEmployeeId();
      const selectedEmployee = this.employees.find((employee) => employee.employeeId === selectedEmployeeId);
      const response = await firstValueFrom(
        this.apiService.get<EmployeeRecord[] | EmployeesResponse>('employees', {
          params: { page: 1, perPage: 20, isActive: 'true', ...(this.getActiveCompanyId() ? { companiesRecordId: this.getActiveCompanyId() } : {}), ...(search ? { search } : {}) },
        })
      );
      const nextEmployees = this.extractEmployees(response)
        .filter((employee) => employee.isActive !== false)
        .sort((left, right) => (left.fullName || '').localeCompare(right.fullName || ''));

      this.employees = selectedEmployee && !nextEmployees.some((employee) => employee.employeeId === selectedEmployee.employeeId)
        ? [selectedEmployee, ...nextEmployees]
        : nextEmployees;

      this.tableFilters = this.buildTableFilters();
    } catch (error) {
      console.error('Error loading employees for attendance attempts', error);
      this.employees = [];
    }
  }

  private extractEmployees(response: EmployeeRecord[] | EmployeesResponse): EmployeeRecord[] {
    if (Array.isArray(response)) {
      return response;
    }
    return response.items || response.data || response.results || response.employees || [];
  }

  private async loadCompanies(): Promise<void> {
    const response = await firstValueFrom(this.apiService.get<CompaniesRecord[] | CompaniesResponse>('companies-records', { params: { isActive: 'true' } }));
    this.companies = (Array.isArray(response) ? response : response.items || response.data || response.results || response.companiesRecords || [])
      .filter((company) => company.isActive !== false)
      .sort((left, right) => left.name.localeCompare(right.name));
  }

  private applyLocalFilters(): void {
    const search = this.searchValue;
    const activeSources = this.getActiveSources();
    const activeDay = this.getActiveDay();
    this.dataSource.data = this.allAttempts.filter((attempt) => {
      if (this.getActiveCompanyId() && attempt.companiesRecordId !== this.getActiveCompanyId()) return false;
      const matchesSource = !activeSources.length || activeSources.includes(attempt.source);
      if (!matchesSource) {
        return false;
      }

      if (activeDay && this.toDateKey(attempt.eventAt, attempt.timezone) !== activeDay) {
        return false;
      }

      if (!search) {
        return true;
      }

      const haystack = [
        attempt.employeeNumber,
        attempt.employeeLabel,
        attempt.sourceLabel,
        attempt.eventTypeLabel,
        attempt.reasonCodeLabel,
        attempt.deviceId,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return haystack.includes(search);
    });
  }

  private normalizeAttempt(attempt: Partial<AttendanceAttemptRecord>): AttendanceAttemptRecord {
    const source = `${attempt.source || 'device'}`.toLowerCase();
    const eventType = this.normalizeEventType(attempt.eventType);
    const faceValidationStatus = attempt.faceValidationStatus || null;
    const reasonCode = attempt.reasonCode || null;
    const isActive = attempt.isActive !== false;
    const employee = this.employees.find((item) => item.employeeId === attempt.employeeId);

    return {
      attendanceAttemptId: attempt.attendanceAttemptId || crypto.randomUUID(),
      center: attempt.center || '',
      employeeId: attempt.employeeId || '',
      employeeNumber: attempt.employeeNumber || employee?.employeeNumber || '-',
      employeeName: attempt.employeeName || employee?.fullName || attempt.employeeNumber || '-',
      companiesRecordId: attempt.companiesRecordId || '',
      orgUnitId: attempt.orgUnitId || null,
      orgPath: attempt.orgPath || null,
      source,
      eventType,
      eventAt: attempt.eventAt || '',
      timezone: attempt.timezone || 'America/Cancun',
      deviceId: attempt.deviceId || null,
      deviceVersion: attempt.deviceVersion || null,
      lat: attempt.lat ?? null,
      lng: attempt.lng ?? null,
      accuracy: attempt.accuracy ?? null,
      faceValidationStatus,
      reasonCode,
      rawPayload: attempt.rawPayload || null,
      isActive,
      employeeLabel: attempt.employeeName || employee?.fullName || attempt.employeeNumber || '-',
      sourceLabel: this.t(`attendanceAttemptsCatalog.sources.${source}`),
      eventTypeLabel: eventType ? this.t(`attendanceAttemptsCatalog.eventTypes.${eventType}`) : '-',
      eventAtLabel: this.formatDateTime(attempt.eventAt, attempt.timezone || 'America/Cancun'),
      faceValidationStatusLabel: faceValidationStatus ? this.t(`attendanceAttemptsCatalog.faceStatuses.${faceValidationStatus}`) : '-',
      reasonCodeLabel: this.getReasonCodeLabel(reasonCode),
      activeLabel: isActive ? this.t('common.status.active') : this.t('common.status.inactive'),
    };
  }

  private buildTableFilters(): TableFilterDefinition<AttendanceAttemptRecord>[] {
    return [
      {
        id: 'company', label: this.t('orgUnitsCatalog.fields.company'), icon: 'business', searchable: true,
        value: this.activeFilters['company'] ?? null,
        options: this.companies.map((company) => ({ value: company.companiesRecordId, label: company.name, searchText: `${company.code || ''} ${company.center || ''}`.trim() })),
      },
      {
        id: 'employee',
        label: this.t('attendanceAttemptsCatalog.filters.employee'),
        placeholder: this.t('attendanceAttemptsCatalog.filters.employeePlaceholder'),
        icon: 'badge',
        searchable: true,
        value: this.activeFilters['employee'] ?? null,
        options: this.employees.map((employee) => ({
          value: employee.employeeId,
          label: employee.fullName || employee.employeeNumber,
          searchText: employee.employeeNumber,
        })),
      },
      {
        id: 'day',
        label: this.t('attendanceAttemptsCatalog.filters.day'),
        placeholder: this.t('attendanceAttemptsCatalog.filters.dayPlaceholder'),
        icon: 'calendar_today',
        type: 'date',
        value: this.activeFilters['day'] ?? null,
      },
      {
        id: 'source',
        label: this.t('attendanceAttemptsCatalog.filters.source'),
        placeholder: this.t('attendanceAttemptsCatalog.filters.sourcePlaceholder'),
        icon: 'memory',
        searchable: false,
        multiple: true,
        value: this.activeFilters['source'] ?? [],
        options: ['device', 'mobile', 'web', 'manual'].map((source) => ({
          value: source,
          label: this.t(`attendanceAttemptsCatalog.sources.${source}`),
        })),
      },
      {
        id: 'reasonCode',
        label: this.t('attendanceAttemptsCatalog.filters.reasonCode'),
        placeholder: this.t('attendanceAttemptsCatalog.filters.reasonCodePlaceholder'),
        icon: 'report_problem',
        searchable: false,
        value: this.activeFilters['reasonCode'] ?? null,
        options: [
          'outside_allowed_zone',
          'face_validation_failed',
          'device_not_registered',
          'device_not_bound',
          'offline_not_allowed',
          'duplicate_attempt',
        ].map((reasonCode) => ({
          value: reasonCode,
          label: this.t(`attendanceAttemptsCatalog.reasonCodes.${reasonCode}`),
        })),
      },
    ];
  }

  private configureTranslations(): void {
    this.actions = [
      {
        id: 'add',
        icon: 'add',
        tooltip: this.t('attendanceAttemptsCatalog.actions.create'),
        colorClass: 'success',
        auth: { resource: 'attendanceAttempts', action: 'create' },
        behavior: 'add',
      },
      {
        id: 'details',
        icon: 'visibility',
        tooltip: this.t('attendanceAttemptsCatalog.actions.view'),
        colorClass: 'info',
        auth: { resource: 'attendanceAttempts', action: 'read' },
        selectionMode: 'single',
      },
      {
        id: 'delete',
        icon: 'delete_outline',
        tooltip: this.t('attendanceAttemptsCatalog.actions.delete'),
        colorClass: 'danger',
        auth: { resource: 'attendanceAttempts', action: 'delete' },
        behavior: 'delete',
        selectionMode: 'single',
      },
    ];

    this.columnDefinitions = [
      { def: 'select', label: this.t('common.select'), type: 'check', visible: true },
      { def: 'employeeNumber', label: this.t('attendanceAttemptsCatalog.fields.employeeNumber'), type: 'text', visible: true },
      { def: 'employeeName', label: this.t('attendanceAttemptsCatalog.fields.employee'), type: 'text', visible: true },
      { def: 'sourceLabel', label: this.t('attendanceAttemptsCatalog.fields.source'), type: 'text', visible: true },
      { def: 'eventTypeLabel', label: this.t('attendanceAttemptsCatalog.fields.eventType'), type: 'text', visible: true },
      { def: 'reasonCodeLabel', label: this.t('attendanceAttemptsCatalog.fields.reasonCode'), type: 'text', visible: true },
      { def: 'eventAtLabel', label: this.t('attendanceAttemptsCatalog.fields.eventAt'), type: 'text', visible: true },
      { def: 'faceValidationStatusLabel', label: this.t('attendanceAttemptsCatalog.fields.faceValidationStatus'), type: 'text', visible: true },
    ];

    this.tableFilters = this.buildTableFilters();
  }

  private getActiveEmployeeId(): string {
    return (this.activeFilters['employee'] as string) || '';
  }

  private getActiveCompanyId(): string { return (this.activeFilters['company'] as string) || ''; }

  private getActiveSources(): string[] {
    const value = this.activeFilters['source'];
    if (Array.isArray(value)) {
      return value.map((item) => `${item}`);
    }

    return value ? [`${value}`] : [];
  }

  private getActiveReasonCode(): string {
    return (this.activeFilters['reasonCode'] as string) || '';
  }

  private getActiveDay(): string {
    return (this.activeFilters['day'] as string) || '';
  }

  private toDateKey(value?: string | null, timezone = 'America/Cancun'): string {
    if (!value) {
      return '';
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return '';
    }

    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).formatToParts(date);
    const getPart = (type: Intl.DateTimeFormatPartTypes) => parts.find((part) => part.type === type)?.value || '';

    return `${getPart('year')}-${getPart('month')}-${getPart('day')}`;
  }

  private normalizeEventType(value?: string | number | null): string {
    if (value === 0 || value === '0') {
      return 'entry';
    }
    if (value === 1 || value === '1') {
      return 'exit';
    }
    if (value === 'entrada') {
      return 'entry';
    }
    if (value === 'salida') {
      return 'exit';
    }
    return String(value ?? '');
  }

  private getReasonCodeLabel(value?: string | null): string {
    if (!value) {
      return '-';
    }

    const key = `attendanceAttemptsCatalog.reasonCodes.${value}`;
    const translated = this.t(key);
    if (translated && translated !== key) {
      return translated;
    }

    return value
      .split('_')
      .filter(Boolean)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
      .join(' ');
  }

  private formatDateTime(value?: string | null, timezone?: string): string {
    if (!value) {
      return '-';
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return value;
    }
    const locale = this.translate.currentLang?.startsWith('en') ? 'en-US' : 'es-MX';
    return new Intl.DateTimeFormat(locale, {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  }

  private hasAccess(action: string): boolean {
    return this.authorizationService.hasAccess({ resource: 'attendanceAttempts', action });
  }

  private showNotification(
    colorName: string,
    text: string,
    placementFrom: MatSnackBarVerticalPosition,
    placementAlign: MatSnackBarHorizontalPosition
  ): void {
    this.snackBar.open(text, '', {
      duration: 2200,
      verticalPosition: placementFrom,
      horizontalPosition: placementAlign,
      panelClass: colorName,
    });
  }

  private t(key: string, params?: Record<string, unknown>): string {
    return this.translate.instant(key, params);
  }
}



